/**
 * Zero-text onboarding: needs are taught by demonstration. A tray slot only
 * exists after its need first arises (nudged onto creature #0 at a friendly
 * pace), and pulses only until its first successful use.
 */
import { EV, type SimEvent } from '../sim/protocol';
import type { SimClient } from '../app/simClient';
import type { Tray } from '../ui/tray';

const HUNGER_AFTER_HATCH_MS = 12_000;
const GRIME_AFTER_FED_MS = 9_000;
const BALL_AFTER_CLEAN_MS = 9_000;

export interface OnboardingFlags {
  hatched: boolean;
  fed: boolean;
  cleaned: boolean;
  played: boolean;
}

export class DiscoveryDirector {
  private hatched = false;
  private fed = false;
  private spongeRevealed = false;
  private cleaned = false;
  private ballRevealed = false;
  private played = false;

  constructor(
    private readonly sim: SimClient,
    private readonly tray: Tray,
    restored?: OnboardingFlags,
  ) {
    if (restored) {
      // a returning caretaker knows their tools — no pulses, no nudges
      this.hatched = restored.hatched;
      this.fed = restored.fed;
      this.cleaned = restored.cleaned;
      this.played = restored.played;
      this.spongeRevealed = restored.cleaned;
      this.ballRevealed = restored.played;
      if (restored.fed || restored.hatched) {
        this.tray.reveal('apple');
        if (restored.fed) this.tray.markUsed('apple');
      }
      if (restored.cleaned) {
        this.tray.reveal('sponge');
        this.tray.markUsed('sponge');
      }
      if (restored.played) {
        this.tray.reveal('ball');
        this.tray.markUsed('ball');
      }
    }
  }

  get flags(): OnboardingFlags {
    return {
      hatched: this.hatched,
      fed: this.fed,
      cleaned: this.cleaned,
      played: this.played,
    };
  }

  handleEvents(events: SimEvent[]): void {
    for (const [kind] of events) {
      switch (kind) {
        case EV.HATCH:
          if (!this.hatched) {
            this.hatched = true;
            window.setTimeout(() => {
              this.sim.nudge('hunger');
              this.tray.reveal('apple');
            }, HUNGER_AFTER_HATCH_MS);
          }
          break;
        case EV.EAT:
          if (!this.fed) {
            this.fed = true;
            this.tray.markUsed('apple');
            window.setTimeout(() => this.sim.nudge('grime'), GRIME_AFTER_FED_MS);
          }
          break;
        case EV.PLOP:
          if (!this.spongeRevealed) {
            this.spongeRevealed = true;
            this.tray.reveal('sponge');
          }
          break;
        case EV.SCRUB_POP:
          if (!this.cleaned) {
            this.cleaned = true;
            this.tray.markUsed('sponge');
            window.setTimeout(() => {
              this.sim.nudge('ball'); // a ball rolls in from offstage — a gift
              if (!this.ballRevealed) {
                this.ballRevealed = true;
                window.setTimeout(() => this.tray.reveal('ball'), 1800);
              }
            }, BALL_AFTER_CLEAN_MS);
          }
          break;
      }
    }
  }

  /** Called by the gesture layer on first player throw/place. */
  markPlayed(): void {
    if (!this.played) {
      this.played = true;
      this.tray.markUsed('ball');
    }
  }
}
